import { Suspense, useEffect, lazy } from 'react'
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { Toaster } from 'sonner'
import Header from './components/Header'
import Footer from './components/Footer'
import BioTuneHomepage from './pages/BioTuneHomepage'
import Features from './pages/Features'
import MealPlanningHomepage from './pages/MealPlanningHomepage'
import SignIn from './pages/auth/SignIn'
import SignUp from './pages/auth/SignUp'
import InvestorPitchDeck from './pages/InvestorPitchDeck'
import Pricing from './pages/Pricing'
import About from './pages/About'
import Contact from './pages/Contact'
import ImageGenerationPage from './pages/ImageGenerationPage'
import NotFound from './pages/NotFound'
import BiotuneMealmateIntegration from './pages/BiotuneMealmateIntegration'
import ThemeDemo from './pages/ThemeDemo'

import { initializeThemeManager, useTheme } from './styles/theme-manager'

// Lazy loaded components
const UserDashboard = lazy(() => import('./pages/dashboard/UserDashboard'))
const OnboardingWizard = lazy(() => import('./pages/onboarding/OnboardingWizard'))
const LabPortal = lazy(() => import('./pages/partners/LabPortal'))
const MealPortal = lazy(() => import('./pages/partners/MealPortal'))
const RestaurantPortal = lazy(() => import('./pages/partners/RestaurantPortal'))

// Legal & Helper Pages
const Privacy = lazy(() => import('./pages/Privacy'))
const Terms = lazy(() => import('./pages/Terms'))
const HipaaNotice = lazy(() => import('./pages/HipaaNotice'))
const Accessibility = lazy(() => import('./pages/Accessibility'))
const Disclaimer = lazy(() => import('./pages/Disclaimer'))
const HowItWorks = lazy(() => import('./pages/HowItWorks'))
const AdvancedHomePage = lazy(() => import('./pages/AILab'))
const MarketVision = lazy(() => import('./pages/MarketVision'))

function App() {
  const { effectiveTheme } = useTheme();

  useEffect(() => {
    initializeThemeManager();
  }, []);

  return (
    <Router>
      <div className={`min-h-screen bg-black transition-colors duration-300 ${effectiveTheme === 'dark' ? 'dark' : ''}`}>
        <Toaster position="top-right" theme="dark" richColors />
        <Header />
        <main>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-black text-theme-primary uppercase tracking-widest text-xs">Initializing BioSync...</div>}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<BioTuneHomepage />} />
              <Route path="/features" element={<Features />} />
              <Route path="/meal-planning" element={<MealPlanningHomepage />} />
              <Route path="/auth/signin" element={<SignIn />} />
              <Route path="/auth/signup" element={<SignUp />} />
              <Route path="/pitch-deck" element={<InvestorPitchDeck />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/images" element={<ImageGenerationPage />} />
              <Route path="/mealmate" element={<BiotuneMealmateIntegration />} />
              <Route path="/theme-demo" element={<ThemeDemo />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/ai-lab" element={<AdvancedHomePage />} />
              <Route path="/market-vision" element={<MarketVision />} />
              
              {/* Partner Portals (Secure Handshake) */}
              <Route path="/partners/labs" element={<LabPortal />} />
              <Route path="/partners/meals" element={<MealPortal />} />
              <Route path="/partners/restaurants" element={<RestaurantPortal />} />

              {/* Secure Dashboards */}
              <Route path="/onboarding" element={<OnboardingWizard />} />
              <Route path="/dashboard" element={<UserDashboard />} />

              {/* Legal Pages */}
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/hipaa" element={<HipaaNotice />} />
              <Route path="/accessibility" element={<Accessibility />} />
              <Route path="/disclaimer" element={<Disclaimer />} />
              
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </main>
        <Footer />
      </div>
    </Router>
  )
}

export default App
